import { useCallback } from "react"
import { graphql, UseMutationConfig } from "react-relay"
import { useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import { formatDiagnosisCodeText } from "@/modules/patients/components/diagnosis-code"
import {
  Ch_generateConsultationReportDocumentInput,
  useGenerateConsultationReportMutation as useGenerateConsultationReportMutationType,
} from "@/modules/patients/detail/consultation-report/services/__generated__/useGenerateConsultationReportMutation.graphql"
import { getDiagnosisCodeByDiagnosisCode } from "@/modules/patients/detail/therapy-prescription/ch/services/getDiagnosisCodeByDiagnosisCode"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const generateConsultationReportDocumentMutation = graphql`
  mutation useGenerateConsultationReportMutation($input: Ch_generateConsultationReportDocumentInput!) {
    ch_generateConsultationReportDocument(input: $input) {
      ch_ConsultationReport {
        id
        fileId
        fileMetadata {
          documentType
          extension
          lengthInKilobytes
        }
        progressNoteId
        patientId
        createdAt
        createdBy
        updatedAt
        updatedBy
        signedAt
        signedBy
        language
        healthcareProviderId
        diagnosisCode
        anamnesis
        recommendedTreatment
        concludedAt
        concludedBy
      }
      errors {
        ... on DocumentDoesNotExist {
          message
        }
      }
    }
  }
`

type Config = Omit<UseMutationConfig<useGenerateConsultationReportMutationType>, "variables"> & {
  params: Omit<Ch_generateConsultationReportDocumentInput, "patientId">
}

const useGenerateConsultationReportMutation = () => {
  const [commit, isFlight] = useMutationWithErrorDialog<useGenerateConsultationReportMutationType>(
    generateConsultationReportDocumentMutation,
  )
  const patientById = usePatientParam()

  const commitHandler = useCallback(
    async (config: Config) => {
      const { code, description } = await getDiagnosisCodeByDiagnosisCode(config.params.diagnosis)
      const formattedDiagnosis = formatDiagnosisCodeText(code, description)

      commit({
        variables: {
          input: {
            patientId: patientById,
            dateOfConsultation: config.params.dateOfConsultation,
            documents: config.params.documents,
            id: config.params.id,
            medications: config.params.medications,
            timeToTreat: config.params.timeToTreat,
            diagnosis: formattedDiagnosis,
          },
        },
        ...config,
      })
    },
    [commit, patientById],
  )

  return [commitHandler, isFlight] as const
}

export { useGenerateConsultationReportMutation }
